'use client';

import { datadogLogs } from '@datadog/browser-logs';

let logsInitialized = false;

const CLIENT_TOKEN = 'pub2fc7844b40e8003811052a1f37387025';
const SITE = 'datadoghq.com';

/**
 * Detect which service context we're running in based on URL path.
 */
function detectService(): string {
  if (typeof window === 'undefined') return 'ai-realtime-game';
  const path = window.location.pathname;
  if (path.startsWith('/tv')) return 'ai-realtime-game-tv';
  if (path.startsWith('/remote')) return 'ai-realtime-game-remote';
  return 'ai-realtime-game';
}

/**
 * Initialize Datadog Logs. Safe to call multiple times — only runs once.
 * Uses the same clientToken as RUM.
 */
export function initDatadogLogs() {
  if (logsInitialized || typeof window === 'undefined') return;

  try {
    datadogLogs.init({
      clientToken: CLIENT_TOKEN,
      site: SITE,
      service: detectService(),
      env: process.env.NEXT_PUBLIC_VERCEL_ENV || 'development',
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });

    logsInitialized = true;
  } catch (err) {
    console.warn('Datadog Logs failed to initialize', err);
  }
}

type LogContext = Record<string, unknown>;

/**
 * Lightweight logger that sends structured logs to Datadog.
 * Falls back to console if Datadog Logs isn't initialized.
 *
 * Usage:
 *   logger.info('tv.room_connected', { roomName });
 *   logger.error('tv.game_state_rpc_failed', { attempt, error: err.message });
 */
export const logger = {
  info(action: string, context?: LogContext) {
    if (logsInitialized) {
      datadogLogs.logger.info(action, context);
    }
    console.log(`[DD] ${action}`, context ?? '');
  },

  warn(action: string, context?: LogContext) {
    if (logsInitialized) {
      datadogLogs.logger.warn(action, context);
    }
    console.warn(`[DD] ${action}`, context ?? '');
  },

  error(action: string, context?: LogContext) {
    if (logsInitialized) {
      datadogLogs.logger.error(action, context);
    }
    console.error(`[DD] ${action}`, context ?? '');
  },
};
